import { useEffect, useState } from 'react'
import { Modal, Descriptions, Button, Tag, Message, Spin, Popconfirm, Empty } from '@arco-design/web-react'
import {
  getUserBindingsApi,
  adminUnbindUserApi,
  type AdminUser,
  type UserBindingInfo,
} from '@/api/user'

interface Props {
  visible: boolean
  user: AdminUser | null
  onCancel: () => void
}

const PROVIDERS: Array<{ key: string; label: string; field: keyof UserBindingInfo }> = [
  { key: 'github', label: 'GitHub', field: 'github_id' },
  { key: 'wechat', label: '微信', field: 'wechat_id' },
  { key: 'linuxdo', label: 'LinuxDo', field: 'linuxdo_id' },
  { key: 'discord', label: 'Discord', field: 'discord_id' },
  { key: 'oidc', label: 'OIDC', field: 'oidc_id' },
  { key: 'telegram', label: 'Telegram', field: 'telegram_id' },
  { key: 'email', label: '邮箱', field: 'email' },
]

export default function UserBindingManagementModal({ visible, user, onCancel }: Props) {
  const [loading, setLoading] = useState(false)
  const [bindings, setBindings] = useState<UserBindingInfo | null>(null)

  const load = async () => {
    if (!user) return
    setLoading(true)
    setBindings(null)
    try {
      const res = await getUserBindingsApi(user.id)
      setBindings(res)
    } catch (err: unknown) {
      Message.warning(err instanceof Error ? err.message : '后端暂未提供绑定接口')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (visible) load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, user])

  const unbind = async (provider: string) => {
    if (!user) return
    try {
      await adminUnbindUserApi(user.id, provider)
      Message.success('已解绑')
      load()
    } catch (err: unknown) {
      Message.error(err instanceof Error ? err.message : '解绑失败')
    }
  }

  const hasAny = bindings && PROVIDERS.some((p) => !!bindings[p.field])

  return (
    <Modal
      title={user ? `第三方绑定 · ${user.username}` : '第三方绑定'}
      visible={visible}
      onCancel={onCancel}
      footer={null}
      style={{ width: 560 }}
    >
      {loading ? (
        <div className="py-10 flex justify-center"><Spin /></div>
      ) : !hasAny ? (
        <Empty description="该用户未绑定任何第三方账号" />
      ) : (
        <Descriptions
          column={1}
          data={PROVIDERS.filter((p) => bindings && !!bindings[p.field]).map((p) => ({
            label: p.label,
            value: (
              <div className="flex items-center justify-between gap-2">
                <Tag color="arcoblue" className="!font-mono">
                  {String(bindings?.[p.field])}
                </Tag>
                {p.key !== 'email' && (
                  <Popconfirm
                    title={`确认解绑 ${p.label}？`}
                    onOk={() => unbind(p.key)}
                  >
                    <Button size="mini" status="warning">解绑</Button>
                  </Popconfirm>
                )}
              </div>
            ),
          }))}
        />
      )}
    </Modal>
  )
}
